/**
 * Every node, cross-tenant (D27's vendor ops view): tenant, site, node and
 * role, the site link, which witness is in force, the release it runs (commit
 * and date, its status, each live production's — D37, `NodeReleaseCell`), the certificate (flagged under 14 days),
 * the clock (flagged over 50 ms) and when it was last heard.
 */
import { describeWitness } from '@norna/api-contract/witness.js';
import type { VendorNodeWire } from '@norna/api-contract/vendor.js';
import { agoText, certState, driftExceeded, linkTone } from '../../lib/vendor.js';
import { NodeReleaseCell } from '../NodeReleaseCell.js';
import { Badge } from '../ui/badge.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type NodesTableProps = {
  readonly nodes: readonly VendorNodeWire[];
  readonly showTenant: boolean;
  /** The clock the certificate and "last seen" are judged against. */
  readonly nowMs: number;
};

export function NodesTable({ nodes, showTenant, nowMs }: NodesTableProps) {
  if (nodes.length === 0) return <p className="py-4 text-sm text-muted-foreground" data-testid="nodes-none">No nodes enrolled.</p>;
  return (
    <Table data-testid="nodes-table">
      <TableHeader>
        <TableRow>
          {showTenant ? <TableHead>Tenant</TableHead> : null}
          <TableHead>Site</TableHead>
          <TableHead>Node</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Link</TableHead>
          <TableHead>Witness</TableHead>
          <TableHead>Release</TableHead>
          <TableHead>Certificate</TableHead>
          <TableHead>Clock</TableHead>
          <TableHead>Last seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {nodes.map((node) => {
          const cert = certState(node.certExpiresAt, nowMs);
          const drifting = driftExceeded(node.clockDriftMs);
          return (
            <TableRow key={`${node.tenantId}/${node.nodeId}`} data-testid="node-row" data-node={node.nodeId}>
              {showTenant ? <TableCell className="font-mono text-xs">{node.tenantId}</TableCell> : null}
              <TableCell className="text-sm">{node.siteName}</TableCell>
              <TableCell className="text-sm font-medium">{node.label}</TableCell>
              <TableCell className="text-sm">{node.role}</TableCell>
              <TableCell>
                <Badge variant={linkTone(node.linkState)} data-testid="node-link">
                  {node.linkState}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">{node.witness === null ? '–' : describeWitness(node.witness)}</TableCell>
              <TableCell>
                <NodeReleaseCell release={node.release} packageHashes={node.packageHashes} />
              </TableCell>
              <TableCell>
                <Badge variant={cert.tone} data-testid="node-cert" data-tone={cert.tone}>
                  {cert.text}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">
                {node.clockDriftMs === null ? (
                  '–'
                ) : drifting ? (
                  <Badge variant="warning" data-testid="node-drift">
                    {node.clockDriftMs} ms
                  </Badge>
                ) : (
                  <span data-testid="node-drift">{node.clockDriftMs} ms</span>
                )}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">{agoText(node.lastSeen, nowMs)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
